import { fetchAndActivate, getRemoteConfig } from "@firebase/remote-config";
import { useQuery } from "@tanstack/react-query";
import { getAnalytics, logEvent } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getValue } from "firebase/remote-config";
import { isEmpty } from "lodash";

export default class FirebaseService {
  private static readonly config = {
    apiKey: `${process.env.REACT_APP_FIREBASE_API_KEY}`,
    authDomain: `${process.env.REACT_APP_FIREBASE_AUTH_DOMAIN}`,
    projectId: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}`,
    storageBucket: `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}`,
    messagingSenderId: `${process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID}`,
    appId: `${process.env.REACT_APP_FIREBASE_APP_ID}`,
    measurementId: `${process.env.REACT_APP_FIREBASE_MEASUREMENT_ID}`,
  };

  private static readonly app = initializeApp(FirebaseService.config);
  private static readonly remoteConfig = getRemoteConfig(FirebaseService.app);
  private static readonly analytics = getAnalytics(FirebaseService.app);
  private static readonly separator = "|";

  constructor() {
    FirebaseService.remoteConfig.defaultConfig = {
      test_key: "Firebase test",
    };
  }

  public async fetchAndActivateRemoteConfig(): Promise<void> {
    await fetchAndActivate(FirebaseService.remoteConfig);
  }
  public static logEvent(
    key: string,
    params?: { [key: string]: unknown }
  ): void {
    logEvent(FirebaseService.analytics, key, params);
  }
  public static getValue(key: string): string {
    const value = getValue(FirebaseService.remoteConfig, key).asString();
    return isEmpty(value) ? key : value;
  }

  public static fireEvent(key: string) {
    const env: string = `${process.env.REACT_APP_ENV}`;
    if (env !== "production") {
      console.log(`>> EVENT: ${key}`);
    }
    logEvent(FirebaseService.analytics, key);
  }

  public static getStringArray(key: string): string[] {
    const mergedString = FirebaseService.getValue(key);
    if (!mergedString) return [];
    return mergedString
      .split(FirebaseService.separator)
      .filter((s) => s.trim());
  }

  // Home Page Keys
  public static recommended_games_title = "recommended_games_title";
  public static games_title = "games_title";
  public static terms_and_conditions_popup_header =
    "terms_and_conditions_popup_header";
  public static terms_and_conditions_popup_text =
    "terms_and_conditions_popup_text";
  public static faq_header = "faq_header";
  public static faq_questions = "faq_questions";
  public static faq_answers = "faq_answers";
  public static contact_header = "contact_header";
  public static contact_phone = "contact_phone";
  public static contact_mail = "contact_mail";
  public static contact_message = "contact_message";
  public static contact_send_button = "contact_send_button";
  public static contact_success_title = "contact_success_title";
  public static contact_success_desc = "contact_success_desc";
  public static contact_fail_title = "contact_fail_title";
  public static contact_fail_desc = "contact_fail_desc";
  public static hub_journey_header = "hub_journey_header";
  public static hub_journey_title = "hub_journey_title";
  public static hub_journey_desc = "hub_journey_desc";
  public static hub_journey_next_task_title = "hub_journey_next_task_title";
  public static journey_back_button = "journey_back_button";
  public static journey_total_text = "journey_total_text";
  public static journey_step_text = "journey_step_text";
  public static journey_poll_title = "journey_poll_title";
  public static journey_poll_success_title = "journey_poll_success_title";
  public static journey_milestone_congrats_title = "journey_milestone_congrats_title";
  public static journey_milestone_congrats_desc = "journey_milestone_congrats_desc";
  public static journey_milestone_congrats_reward_title = "journey_milestone_congrats_reward_title";
  public static journey_milestone_congrats_now_button = "journey_milestone_congrats_now_button";
  public static journey_milestone_congrats_later_button = "journey_milestone_congrats_later_button";
  public static journey_milestone_congrats_next_level_note = "journey_milestone_congrats_next_level_note";
  public static journey_milestone_congrats_now_loading_note = "journey_milestone_congrats_now_loading_note";
  public static journey_milestone_congrats_now_note = "journey_milestone_congrats_now_note";
  public static journey_milestone_congrats_redirect_note = "journey_milestone_congrats_redirect_note";
  public static journey_milestone_congrats_later_note = "journey_milestone_congrats_later_note";
  public static login_title = "login_title";
  public static login_terms_text = "login_terms_text";
  public static login_button = "login_button";
  public static login_warning = "login_warning";
  public static otp_title = "otp_title";
  public static otp_button = "otp_button";
  public static otp_resend_button = "otp_resend_button";
  public static otp_warning = "otp_warning";
  public static journey_alert_title = "journey_alert_title";
  public static journey_alert_button = "journey_alert_button";
  public static games_alert_title = "games_alert_title";
  public static games_alert_button = "games_alert_button";
  public static error_title = "error_title";
  public static error_desc = "error_desc";
  public static error_redirect_note = "error_redirect_note";

}

export function useRemoteConfig() {
  return useQuery({
    queryKey: ["REMOTE_CONFIG"],
    queryFn: async () => {
      const firebaseService = new FirebaseService();
      await firebaseService.fetchAndActivateRemoteConfig();
      return true;
    },
  });
}
